
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import MetricsListBox from '@/components/MetricsListBox.vue'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import { ngtorchRuns } from '@/mixins/ngtorchRuns.js'

export default {
  mixins: [ngtorchRuns],
  components: {
    MetricsListBox,
    NgtorchRunChip
  },
  data () {
    return {
      recentModelsPage: 1,
      recentPlansPage: 1,
      accessiblePlansPage: 1,
      allModelsPage: 1,
      accessibleModelsPage: 1,
      userCreatedModelsPage: 1,
      allUploadsPage: 1,
      userUploadsPage: 1,
      allStageActivityPage: 1,
      userStageActivityPage: 1,
      accessibleEkrmsPage: 1,
      userCreatedEkrmsPage: 1,
      pageSize: 4,
      showOnlyMyPlans: false,
      showOnlyMyModels: false,
      showOnlyMyUploads: false,
      showOnlyMyStageActivity: false,
      showOnlyMyEkrms: false
    }
  },
  mounted () {
    if (this.isExternalUser) {
      this.showOnlyMyPlans = false
    }
    if (this.authUser && this.authUser.uid) {
      this.loadUserSimulations()
      this.loadAccessibleRecentSimulations()
      this.loadRecentModels()
      this.loadAccessibleRecentModels()
      this.loadUserCreatedModels()
      if (this.authUser.isAdmin || this.authUser.isManager || this.authUser.isScientist || this.authUser.isSupervisor) {
        this.loadAllRecentModels()
      }
      if (this.authUser.isAdmin || this.authUser.isManager || this.authUser.isScientist || this.authUser.isSupervisor || this.isEkrmManager) {
        this.loadAccessibleRecentEkrms()
        this.loadUserCreatedEkrms()
      }
      if (this.authUser.isAdmin || this.authUser.isManager || this.authUser.isScientist || this.authUser.isSupervisor || this.isDataUploader) {
        this.loadAllRecentUploads()
        this.loadUserRecentUploads()
        this.loadAllRecentStageActivity()
        this.loadUserRecentStageActivity()
      }
    }
  },
  watch: {
    showOnlyMyPlans (newVal) {
      if (this.isExternalUser && newVal) {
        this.showOnlyMyPlans = false
        return
      }
      this.recentPlansPage = 1
      this.accessiblePlansPage = 1
    },
    showOnlyMyModels () {
      this.userCreatedModelsPage = 1
      this.accessibleModelsPage = 1
    },
    showOnlyMyUploads () {
      this.userUploadsPage = 1
      this.allUploadsPage = 1
    },
    showOnlyMyStageActivity () {
      this.userStageActivityPage = 1
      this.allStageActivityPage = 1
    },
    showOnlyMyEkrms () {
      this.userCreatedEkrmsPage = 1
      this.accessibleEkrmsPage = 1
    }
  },
  methods: {
    // A Budget Simulator in the list stands for its grid points, which are the plans that ran.
    homePlanOverrides (plan) {
      if (plan.type === 'budget_simulation') return this.ngtorchBudgetSimulationOverrides(plan.client_id, plan.id)
      return [this.ngtorchPlanOverride(plan.client_id, plan)]
    },
    ...mapActions('simulations', [
      'loadUserSimulations',
      'loadAccessibleRecentSimulations'
    ]),
    ...mapActions('clients', [
      'setCurrentClient',
      'loadRecentModels',
      'loadAllRecentModels',
      'loadAccessibleRecentModels',
      'loadUserCreatedModels',
      'loadAllRecentUploads',
      'loadUserRecentUploads',
      'loadAllRecentStageActivity',
      'loadUserRecentStageActivity',
      'loadAccessibleRecentEkrms',
      'loadUserCreatedEkrms'
    ]),
    formatDateFromNow (date) {
      return moment(date).fromNow()
    },
    getClientName (clientId) {
      let client = this.clients.find(c => c.id === clientId)
      if (!client) {
        client = this.groupClients.find(c => c.id === clientId)
      }
      return client ? client.name : ''
    },
    isBudgetSimulation (plan) {
      return plan.type === 'budget_simulation'
    },
    // A Budget Simulation has no results of its own; it is flagged notified once all its grid points are.
    isPlanProcessed (plan) {
      if (this.isBudgetSimulation(plan)) return !!plan.notified
      return plan && (plan.results || plan.hasResults)
    },
    // A Budget Simulation's page shows its progress while its grid points compute, so it opens anyway.
    isPlanClickable (plan) {
      return this.isBudgetSimulation(plan) || this.isPlanProcessed(plan)
    },
    // The icon the navigation menu gives the tool the plan came from. Plans without a type
    // predate the Calculator and are optimizations.
    planTypeIcon (plan) {
      if (this.isBudgetSimulation(plan)) return 'mdi-chart-bell-curve-cumulative'
      if (plan.type === 'plan_simulation') return 'mdi-calculator'
      return 'mdi-engine'
    },
    planTypeName (plan) {
      if (this.isBudgetSimulation(plan)) return this.$t('views.App.budgetSimulator')
      if (plan.type === 'plan_simulation') return this.$t('views.App.planCalculator')
      return this.$t('views.App.planOptimizer')
    },
    openPlan (plan) {
      if (this.isBudgetSimulation(plan)) {
        this.setCurrentClient(plan.client_id)
        this.$router.push({ name: 'budgetSimulationDetails', params: { id: plan.id } })
      } else {
        this.navigateToSimulation(plan)
      }
    },
    navigateToSimulation (plan) {
      this.setCurrentClient(plan.client_id)
      this.$router.push({
        name: 'simulationDetails',
        params: {
          simulation: plan.id,
          client: plan.client_id
        }
      })
    },
    getCreatorName (plan) {
      if (plan.created_by_name) {
        return plan.created_by_name
      }
      if (plan.created_by && plan.created_by.indexOf('_') !== -1) {
        return plan.created_by.slice(plan.created_by.indexOf('_') + 1)
      }
      return plan.created_by || 'Unknown'
    },
    navigateToModel (model) {
      this.setCurrentClient(model.clientId)
      this.$router.push({
        name: 'modelReport',
        params: {
          client: model.clientId
        }
      })
    },
    navigateToModelById (model) {
      this.setCurrentClient(model.id)
      this.$router.push({
        name: 'modelReport',
        params: {
          client: model.id
        }
      })
    },
    getCreatorNameForModel (model) {
      if (model.created_by_name) {
        return model.created_by_name
      }
      if (model.created_by && model.created_by.indexOf('_') !== -1) {
        return model.created_by.slice(model.created_by.indexOf('_') + 1)
      }
      return model.created_by || this.$t('views.Home.unknown')
    },
    getModelTypeLabel (type) {
      const typeMap = {
        'mmm': this.$t('views.Home.modelTypeAdHoc'),
        'custom': this.$t('views.Home.modelTypeCustomStandard'),
        'ekrm': this.$t('views.Home.modelTypeCustomEkrm')
      }
      return typeMap[type] || ''
    },
    getClientType (clientId) {
      let client = this.clients.find(c => c.id === clientId)
      if (!client) {
        client = this.groupClients.find(c => c.id === clientId)
      }
      return client ? client.type : ''
    },
    // Pagination handlers
    handleRecentModelsPageChange (newPage) {
      this.recentModelsPage = newPage
      // Check if we need to fetch more data from server
      const totalLoadedPages = Math.ceil(this.recentModels.length / this.pageSize)
      // If we're on page 4 of 5 (approaching end), fetch more
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.recentModelsHasMore) {
        const lastVisible = this.$store.state.clients.recentModelsLastVisible
        this.loadRecentModels({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleRecentPlansPageChange (newPage) {
      this.recentPlansPage = newPage
      const totalLoadedPages = Math.ceil(this.recentPlanOptimizations.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.simulations.userSimulationsHasMore) {
        const lastVisible = this.$store.state.simulations.userSimulationsLastVisible
        this.loadUserSimulations({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleAccessiblePlansPageChange (newPage) {
      this.accessiblePlansPage = newPage
      const totalLoadedPages = Math.ceil(this.accessibleRecentSimulations.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.simulations.accessibleRecentSimulationsHasMore) {
        const lastVisible = this.$store.state.simulations.accessibleRecentSimulationsLastVisible
        this.loadAccessibleRecentSimulations({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleAllModelsPageChange (newPage) {
      this.allModelsPage = newPage
      const totalLoadedPages = Math.ceil(this.allRecentModels.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.allRecentModelsHasMore) {
        const lastVisible = this.$store.state.clients.allRecentModelsLastVisible
        this.loadAllRecentModels({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleAccessibleModelsPageChange (newPage) {
      this.accessibleModelsPage = newPage
      const totalLoadedPages = Math.ceil(this.accessibleRecentModels.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.accessibleRecentModelsHasMore) {
        const lastVisible = this.$store.state.clients.accessibleRecentModelsLastVisible
        this.loadAccessibleRecentModels({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleUserCreatedModelsPageChange (newPage) {
      this.userCreatedModelsPage = newPage
      const totalLoadedPages = Math.ceil(this.userCreatedModels.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.userCreatedModelsHasMore) {
        const lastVisible = this.$store.state.clients.userCreatedModelsLastVisible
        this.loadUserCreatedModels({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleAllUploadsPageChange (newPage) {
      this.allUploadsPage = newPage
      const totalLoadedPages = Math.ceil(this.allRecentUploads.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.allRecentUploadsHasMore) {
        const lastVisible = this.$store.state.clients.allRecentUploadsLastVisible
        this.loadAllRecentUploads({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleUserUploadsPageChange (newPage) {
      this.userUploadsPage = newPage
      const totalLoadedPages = Math.ceil(this.userRecentUploads.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.userRecentUploadsHasMore) {
        const lastVisible = this.$store.state.clients.userRecentUploadsLastVisible
        this.loadUserRecentUploads({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    navigateToDataUpload (upload) {
      this.setCurrentClient(upload.client_id)
      this.$router.push({
        name: 'dataUpload',
        params: {
          client: upload.client_id
        }
      })
    },
    getUploadActivityLabel (upload) {
      if (!upload.lastActivityDate) {
        return this.$t('views.Home.uploaded') + ' ' + this.formatDateFromNow(upload.dateUploaded)
      }
      const uploadTime = upload.dateUploaded ? upload.dateUploaded.getTime() : 0
      const approvalTime = upload.approvedDate ? upload.approvedDate.getTime() : 0
      if (approvalTime > uploadTime) {
        return this.$t('views.Home.activityApproved') + ' ' + this.formatDateFromNow(upload.lastActivityDate)
      }
      return this.$t('views.Home.uploaded') + ' ' + this.formatDateFromNow(upload.lastActivityDate)
    },
    handleAllStageActivityPageChange (newPage) {
      this.allStageActivityPage = newPage
      const totalLoadedPages = Math.ceil(this.allRecentStageActivity.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.allRecentStageActivityHasMore) {
        const lastVisible = this.$store.state.clients.allRecentStageActivityLastVisible
        this.loadAllRecentStageActivity({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleUserStageActivityPageChange (newPage) {
      this.userStageActivityPage = newPage
      const totalLoadedPages = Math.ceil(this.userRecentStageActivity.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.userRecentStageActivityHasMore) {
        const lastVisible = this.$store.state.clients.userRecentStageActivityLastVisible
        this.loadUserRecentStageActivity({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    navigateToModelProcess (activity) {
      this.setCurrentClient(activity.client_id)
      this.$router.push({
        name: 'modelingProcess',
        params: {
          client: activity.client_id
        }
      })
    },
    getStageActivityLabel (activity) {
      const typeLabels = {
        'approval': this.$t('views.Home.activityApproved'),
        'reversal': this.$t('views.Home.activityReversed'),
        'upload': this.$t('views.Home.activityUploaded'),
        'feedback': this.$t('views.Home.activityFeedback'),
        'feedbackReply': this.$t('views.Home.activityFeedbackReply')
      }
      return typeLabels[activity.activityType] || activity.activityType
    },
    handleAccessibleEkrmsPageChange (newPage) {
      this.accessibleEkrmsPage = newPage
      const totalLoadedPages = Math.ceil(this.accessibleRecentEkrms.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.accessibleRecentEkrmsHasMore) {
        const lastVisible = this.$store.state.clients.accessibleRecentEkrmsLastVisible
        this.loadAccessibleRecentEkrms({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    handleUserCreatedEkrmsPageChange (newPage) {
      this.userCreatedEkrmsPage = newPage
      const totalLoadedPages = Math.ceil(this.userCreatedEkrms.length / this.pageSize)
      if (newPage >= totalLoadedPages - 1 && this.$store.state.clients.userCreatedEkrmsHasMore) {
        const lastVisible = this.$store.state.clients.userCreatedEkrmsLastVisible
        this.loadUserCreatedEkrms({ append: true, startAfter: lastVisible, limit: 20 })
      }
    },
    navigateToEkrm (ekrm) {
      this.$router.push({
        name: 'manageEkrm'
      })
    },
    getCreatorNameForEkrm (ekrm) {
      if (ekrm.created_by && ekrm.created_by.indexOf('_') !== -1) {
        return ekrm.created_by.slice(ekrm.created_by.indexOf('_') + 1)
      }
      return ekrm.created_by || 'Unknown'
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser',
      'isDataUploader',
      'isEkrmManager',
      'shouldShowOnlyModelReport',
      'isExternalUser'
    ]),
    ...mapGetters('clients', [
      'currentClientObject',
      'clients',
      'groupClients',
      'recentModels',
      'recentModelsLoading',
      'allRecentModels',
      'allRecentModelsLoading',
      'accessibleRecentModels',
      'accessibleRecentModelsLoading',
      'userCreatedModels',
      'userCreatedModelsLoading',
      'allRecentUploads',
      'allRecentUploadsLoading',
      'userRecentUploads',
      'userRecentUploadsLoading',
      'allRecentStageActivity',
      'allRecentStageActivityLoading',
      'userRecentStageActivity',
      'userRecentStageActivityLoading',
      'accessibleRecentEkrms',
      'accessibleRecentEkrmsLoading',
      'userCreatedEkrms',
      'userCreatedEkrmsLoading'
    ]),
    ...mapGetters('simulations', [
      'userSimulations',
      'userSimulationsLoading',
      'accessibleRecentSimulations',
      'accessibleRecentSimulationsLoading'
    ]),
    // Paginated data slices
    recentModelsPageData () {
      const start = (this.recentModelsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.recentModels.slice(start, end)
    },
    recentModelsHasMore () {
      const totalPages = Math.ceil(this.recentModels.length / this.pageSize)
      const hasMorePages = this.recentModelsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.recentModelsHasMore
      return hasMorePages || hasMoreFromServer
    },
    recentPlanOptimizations () {
      return this.userSimulations
    },
    recentPlansPageData () {
      const start = (this.recentPlansPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.recentPlanOptimizations.slice(start, end)
    },
    recentPlansHasMore () {
      const totalPages = Math.ceil(this.recentPlanOptimizations.length / this.pageSize)
      const hasMorePages = this.recentPlansPage < totalPages
      const hasMoreFromServer = this.$store.state.simulations.userSimulationsHasMore
      return hasMorePages || hasMoreFromServer
    },
    filteredAccessibleSimulations () {
      if (this.isExternalUser && this.authUser && this.authUser.uid) {
        var uid = this.authUser.uid
        return this.accessibleRecentSimulations.filter(function (sim) { return sim.created_by_id === uid || sim.showToExternalUsers })
      }
      return this.accessibleRecentSimulations
    },
    accessiblePlansPageData () {
      const start = (this.accessiblePlansPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredAccessibleSimulations.slice(start, end)
    },
    ngtorchRunsPlans () {
      const plans = this.showOnlyMyPlans ? this.recentPlansPageData : this.accessiblePlansPageData
      return plans.map(plan => ({ ...plan, clientId: plan.client_id }))
    },
    accessiblePlansHasMore () {
      const totalPages = Math.ceil(this.filteredAccessibleSimulations.length / this.pageSize)
      const hasMorePages = this.accessiblePlansPage < totalPages
      const hasMoreFromServer = this.$store.state.simulations.accessibleRecentSimulationsHasMore
      return hasMorePages || hasMoreFromServer
    },
    allModelsPageData () {
      const start = (this.allModelsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.allRecentModels.slice(start, end)
    },
    allModelsHasMore () {
      const totalPages = Math.ceil(this.allRecentModels.length / this.pageSize)
      const hasMorePages = this.allModelsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.allRecentModelsHasMore
      return hasMorePages || hasMoreFromServer
    },
    accessibleModelsPageData () {
      const start = (this.accessibleModelsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.accessibleRecentModels.slice(start, end)
    },
    accessibleModelsHasMore () {
      const totalPages = Math.ceil(this.accessibleRecentModels.length / this.pageSize)
      const hasMorePages = this.accessibleModelsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.accessibleRecentModelsHasMore
      return hasMorePages || hasMoreFromServer
    },
    userCreatedModelsPageData () {
      const start = (this.userCreatedModelsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.userCreatedModels.slice(start, end)
    },
    userCreatedModelsHasMore () {
      const totalPages = Math.ceil(this.userCreatedModels.length / this.pageSize)
      const hasMorePages = this.userCreatedModelsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.userCreatedModelsHasMore
      return hasMorePages || hasMoreFromServer
    },
    allUploadsPageData () {
      const start = (this.allUploadsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.allRecentUploads.slice(start, end)
    },
    allUploadsHasMore () {
      const totalPages = Math.ceil(this.allRecentUploads.length / this.pageSize)
      const hasMorePages = this.allUploadsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.allRecentUploadsHasMore
      return hasMorePages || hasMoreFromServer
    },
    userUploadsPageData () {
      const start = (this.userUploadsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.userRecentUploads.slice(start, end)
    },
    userUploadsHasMore () {
      const totalPages = Math.ceil(this.userRecentUploads.length / this.pageSize)
      const hasMorePages = this.userUploadsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.userRecentUploadsHasMore
      return hasMorePages || hasMoreFromServer
    },
    allStageActivityPageData () {
      const start = (this.allStageActivityPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.allRecentStageActivity.slice(start, end)
    },
    allStageActivityHasMore () {
      const totalPages = Math.ceil(this.allRecentStageActivity.length / this.pageSize)
      const hasMorePages = this.allStageActivityPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.allRecentStageActivityHasMore
      return hasMorePages || hasMoreFromServer
    },
    userStageActivityPageData () {
      const start = (this.userStageActivityPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.userRecentStageActivity.slice(start, end)
    },
    userStageActivityHasMore () {
      const totalPages = Math.ceil(this.userRecentStageActivity.length / this.pageSize)
      const hasMorePages = this.userStageActivityPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.userRecentStageActivityHasMore
      return hasMorePages || hasMoreFromServer
    },
    accessibleEkrmsPageData () {
      const start = (this.accessibleEkrmsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.accessibleRecentEkrms.slice(start, end)
    },
    accessibleEkrmsHasMore () {
      const totalPages = Math.ceil(this.accessibleRecentEkrms.length / this.pageSize)
      const hasMorePages = this.accessibleEkrmsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.accessibleRecentEkrmsHasMore
      return hasMorePages || hasMoreFromServer
    },
    userCreatedEkrmsPageData () {
      const start = (this.userCreatedEkrmsPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.userCreatedEkrms.slice(start, end)
    },
    userCreatedEkrmsHasMore () {
      const totalPages = Math.ceil(this.userCreatedEkrms.length / this.pageSize)
      const hasMorePages = this.userCreatedEkrmsPage < totalPages
      const hasMoreFromServer = this.$store.state.clients.userCreatedEkrmsHasMore
      return hasMorePages || hasMoreFromServer
    },
    skin () {
      // Duplicated in App.vue
      // TODO: Unify
      if (window.location.hostname === 'predixon.noisegrasp.com') {
        return {
          name: 'Predixon'
        }
      } else if (window.location.hostname === 'outflink.noisegrasp.com') {
        return {
          name: 'Outflink'
        }
      } else if (window.location.hostname === 'deloitte.noisegrasp.com') {
        return {
          name: 'Deloitte Marketing Mix Modeling'
        }
      } else if (window.location.hostname === 'nisum.noisegrasp.com') {
        return {
          name: 'Nisum Marketing Mix Modeling'
        }
      } else if (window.location.hostname === 'mckinsey.noisegrasp.com') {
        return {
          name: 'McKinsey Marketing Mix Modeling'
        }
      } else if (window.location.hostname === 'comscore.noisegrasp.com') {
        return {
          name: 'Comscore Marketing Mix Modeling'
        }
      } else {
        return {
          name: 'NoiseGrasp'
        }
      }
    }
  }
}
